import React, { useMemo, useState } from 'react'

import { Formik, FormikValues } from 'formik'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import parties from '../parties'
import { useApplicantsAndContactProfiles } from '../useApplicants'
import AddContactDrawerStepper from './AddContactDrawerStepper'
import { setAddContactDrawerOpen } from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import { ProfileTypes } from '@/constants/applicationProfile'
import { useGetApplicationSsnsQuery } from '@/services/application'
import { useListCompaniesQuery } from '@/services/companies'
import { useGetStaffUsersQuery } from '@/services/user'
import {
  useCreateApplicationPartyMutation,
  useListApplicationPartiesQuery,
  useUpdateApplicationPartyMutation,
} from '@/services/v2-application/applicationParties'
import {
  useCreateNonApplicantMutation,
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { ApplicationPartyType, Company } from '@/types/unlock'
import { evaluate } from '@/utils/format'
import { toastDefaultOptions } from '@/utils/toast'

const typeToGroupMap = new Map([
  ['sales', 'sales'],
  ['processing', 'processing'],
  ['underwriter', 'underwriting'],
  ['closer', 'closing'],
  ['post_closer', 'post-closing'],
  ['asset_manager', 'uam-review'],
])

const getTrusteeState = (trustee_state, address) => {
  if (trustee_state) return trustee_state

  if (address) {
    const addressSplit = address.split(' ')
    if (addressSplit.length >= 2 && addressSplit[addressSplit.length - 2]?.length === 2) {
      return addressSplit[addressSplit.length - 2]
    }
  }

  return ''
}

export const AddFileContactForm = ({ application }) => {
  const dispatch = useAppDispatch()
  const [message, setMessage] = useState('')
  const [isError, setIsError] = useState(false)
  const [selectedType, setSelectedType] = useState<ApplicationPartyType>()
  const { data: companies } = useListCompaniesQuery()
  const { data: staffUsers } = useGetStaffUsersQuery()
  const { data: applicationSsn } = useGetApplicationSsnsQuery(application?.externalId, {
    skip: !application?.uuid,
  })
  const { data: applicationParties } = useListApplicationPartiesQuery(application?.uuid)
  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: nonApplicants } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const [createParty] = useCreateApplicationPartyMutation()
  const [updateParty] = useUpdateApplicationPartyMutation()
  const [createNonApplicant] = useCreateNonApplicantMutation()

  const { emailIsDuplicated } = useApplicantsAndContactProfiles(application?.uuid)

  const marriedOptions = useMemo(() => {
    const appProfiles = (applicationProfiles?.results || []).filter(
      (appProfile) => !appProfile.married_to_profile
    )

    const nonAppProfiles = (nonApplicants?.results || []).filter(
      (appProfile) => !appProfile.married_to_profile
    )

    const options = [...appProfiles, ...nonAppProfiles].map((appProfile) => ({
      label: appProfile.profile.full_name,
      value: appProfile.id,
    }))

    options.unshift({ label: 'Not Married', value: null })

    return options
  }, [applicationProfiles, nonApplicants])

  const selectedCategory = useMemo(
    () => parties.find((it) => it.type === selectedType)?.category,
    [selectedType]
  )

  const partyOptions = useMemo(() => {
    return parties
      .filter((it) => it.category !== 'user')
      .map((it) => ({
        label: it.name,
        value: it.type,
        predefined: it.predefined,
        category: it.category,
      }))
      ?.sort((a, b) => (a.label < b.label ? -1 : 1))
  }, [])

  const directoryOptions = useMemo(() => {
    if (selectedCategory === 'company') {
      if (!companies) return []
      return companies
        .filter((company) => company.type === selectedType)
        .map((company) => {
          const { name, bor_state, type, trustee_state, address } = company

          if (type === 'trustee') {
            return {
              label: `${bor_state ? `${bor_state} - ` : ''}${name} - ${getTrusteeState(
                trustee_state,
                address
              )}`,
              value: company.uuid,
            }
          }
          return {
            label: `${bor_state ? `${bor_state} - ` : ''}${name}`,
            value: company.uuid,
          }
        })
    } else if (selectedCategory === 'user') {
      if (!staffUsers) return []

      const groupName = typeToGroupMap.get(selectedType)
      if (!groupName) return []

      return staffUsers
        .filter((user) => user.groups.find((group) => group.name === groupName))
        .map((user) => ({
          label: user.data?.full_name || user.email,
          value: user.id,
        }))
    } else {
      return []
    }
  }, [companies, selectedType, selectedCategory])

  const resetDrawer = () => {
    setMessage('')
    setIsError(false)
    dispatch(setAddContactDrawerOpen(false))
    toast.success('The contact has been added successfully.', { ...toastDefaultOptions })
  }

  const createContact = (values) => {
    let payload

    if (values.currentMode === 'directory') {
      if (selectedCategory === 'company') {
        const companyData: Company = companies.find((it) => it.uuid === values.directory_name)

        const {
          type,
          name,
          contact_name = '',
          phone,
          email = '',
          address,
          bor_license,
          bor_state,
        } = companyData

        payload = {
          name,
          type,
          phone,
          email,
          contact_name,
          address,
          bor_license,
          bor_state,
        }
      } else if (selectedCategory === 'user') {
        const user = staffUsers.find((it) => String(it.id) === values.directory_name)

        payload = {
          contact_name: user.data?.full_name,
          phone: user.data?.phone,
          email: user.email,
        }
      }
    } else {
      payload = {
        application: application.externalId,
        name: '',
        phone: values.phone,
        email: values.email,
        contact_name: values.contact_name,
        address: values.address,
        social_security_number: '',
        born_on: null,
        id_expiration_date: null,
        is_us_citizen_or_green_card_holder: null,
        title: values.title,
        hoa_subdivision_name: values.hoa_subdivision_name,
        is_manually_created: true,
      }
    }

    const existingParty = applicationParties?.results.find(
      (it) => it.application_party_type === selectedType
    )

    if (existingParty) {
      updateParty({
        applicationUUID: application.uuid,
        applicationPartyUUID: existingParty.uuid,
        party: {
          uuid: existingParty.party.uuid,
          ...payload,
        },
      })
        .unwrap()
        .then(() => {
          resetDrawer()
        })
        .catch(() => {
          toast.error('Something went wrong', { ...toastDefaultOptions })
        })
    } else {
      createParty({
        applicationUUID: application.uuid,
        type: values.contact_type,
        party: {
          ...payload,
        },
      })
        .unwrap()
        .then(() => {
          resetDrawer()
        })
        .catch(() => {
          toast.error('Something went wrong', { ...toastDefaultOptions })
        })
    }
  }

  const handleCreateNonApplicant = (values) => {
    const payload = {
      address: {
        street: values.street,
        unit_number: values.unit_number,
        city: values.city,
        state: values.state,
        zip_code: values.zip_code,
      },
      profile: {
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        social_security_number: values.social_security_number,
        is_us_citizen_or_green_card_holder: values.is_us_citizen_or_green_card_holder,
        photo_id_expire: values.photo_id_expire || null,
        born_on: values.born_on || null,
      },
      married_to_profile: values.married_to !== 'null' ? values.married_to : null,
      profile_type: values.contact_type,
    }
    createNonApplicant({ applicationUuid: application.uuid, ...payload })
      .unwrap()
      .then(() => {
        resetDrawer()
      })
      .catch(() => {
        toast.error('Something went wrong', { ...toastDefaultOptions })
      })
  }

  const handleAddFileContact = (values, actions) => {
    try {
      actions.setSubmitting(true)

      if ([ProfileTypes.CO_OWNER, ProfileTypes.NOO].includes(values.contact_type)) {
        handleCreateNonApplicant(values)
      } else {
        createContact(values)
      }
    } catch (error) {
      setIsError(true)
      setMessage('There was an error on the request, please try again')
    } finally {
      actions.setSubmitting(false)
    }
  }

  const isCoOwnerOrNoo = (contact_type) =>
    contact_type === ProfileTypes.NOO || contact_type === ProfileTypes.CO_OWNER
  const isHOA = (contact_type) => contact_type === 'hoa'

  const isCoOwnerOrNooOrHOA = (contact_type) =>
    contact_type === 'non_owning_occupant' || contact_type === 'co_owner' || contact_type === 'hoa'

  const validationSchema = Yup.object().shape({
    currentMode: Yup.string().required(),
    manual_name: Yup.string().when('currentMode', {
      is: (currentMode) => currentMode !== 'directory' && !isCoOwnerOrNoo,
      then: (schema) => schema.required(),
    }),
    contact_name: Yup.string().when('contact_type', {
      is: !isCoOwnerOrNoo,
      then: (schema) => schema.required(),
    }),
    first_name: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('First Name field is required.'),
    }),
    last_name: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('Last Name field is required.'),
    }),
    married_to: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('Married To field is required.'),
    }),
    street: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('Street field is required.'),
    }),
    city: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('City field is required.'),
    }),
    state: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('State field is required.'),
    }),
    zip_code: Yup.string().when('contact_type', {
      is: isCoOwnerOrNoo,
      then: (schema) => schema.required('Zip Code field is required.'),
    }),
    directory_name: Yup.string().when('currentMode', {
      is: (currentMode) => currentMode === 'directory',
      then: (schema) => schema.required('Directory Name field is required.'),
    }),
    social_security_number: Yup.string()
      .matches(/^(\d{9})?$/, 'Please enter a valid SSN')
      .test('not-invalid-ssn', 'SSN already exists on application.', function (value) {
        if (!value || !applicationSsn) return true
        return !applicationSsn['ssns'].includes(value)
      })
      .optional(),
    phone: Yup.string().nullable().optional(),
    born_on: Yup.date().nullable().optional(),
    id_expiration_date: Yup.date().nullable().optional(),
    is_us_citizen_or_green_card_holder: Yup.boolean().optional(),
    title: Yup.string().when('contact_type', {
      is: isHOA,
      then: (schema) => schema.required('Title is required'),
    }),
    hoa_subdivision_name: Yup.string(),
    email: Yup.string()
      .required('Email is required')
      .email('Email is not valid')
      .test(
        'not-repeated-email',
        'Email address already exists and non-applicant can only share their spouse’s email.',
        function (value) {
          // If no value provided, skip further checks
          if (!value) return true

          if (
            this.parent?.married_to &&
            applicationProfiles?.results?.find(
              (appProfile) => appProfile.id == this.parent?.married_to
            )?.profile?.email === value
          ) {
            return true
          }

          const profile = { married_to_profile: this.parent?.married_to }
          return !emailIsDuplicated(profile, value)
        }
      ),
  })

  return (
    <Formik<FormikValues>
      initialValues={{}}
      onSubmit={handleAddFileContact}
      validationSchema={validationSchema}
      validateOnChange
    >
      {(formikProps) => {
        return (
          <AddContactDrawerStepper
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedCategory={selectedCategory}
            applicationExternalId={application?.externalId}
            applicationProfiles={applicationProfiles?.results || []}
            companies={companies || []}
            marriedOptions={marriedOptions}
            partyOptions={partyOptions}
            directoryOptions={directoryOptions}
            isError={isError}
            errorMessage={message}
            formikProps={formikProps}
          />
        )
      }}
    </Formik>
  )
}
